import * as PIXI from 'pixi.js';
import { DebugGridView } from 'js/bubbleCore/view/DebugGridView';
import { DebugHexSpaceView } from 'js/bubbleCore/view/DebugHexSpaceView';
import { Bubble } from '../bubbles/types';
import { BubblesView } from 'js/bubbleCore/view/BubblesView';
import { BubbleGameBoard } from '../BubbleGameBoard';
import { ShooterPointerView } from './ShooterPointerView';
import { ShooterView } from './ShooterView';
import { getHexPixelSize } from 'js/bubbleCore/view/utils/getHexPixelSize';
import { IHexPixelSize } from 'js/bubbleCore/view/types';
import { BouncersView } from 'js/bubbleCore/view/BouncersView';
import { PointZonesView } from 'js/bubbleCore/view/PointZonesView';
import { DeathLineView } from 'js/bubbleCore/view/entities/DeathLineView';
import { InputCatcher } from 'js/bubbleCore/view/InputCatcher';
import { getWholeBoardPixelSize } from 'js/bubbleCore/view/utils/getWholeBoardPixelSize';
import { IRectangle } from 'framework/utils/math-utils';
import { ITestShootResult } from 'js/bubbleCore/handlers/testShoot';
import { connectSignalToHasEventHandlers } from 'js/bubbleCore/events';
import { Layout } from 'js/layouts/GameBoardLayout';
import { TweenManager } from 'framework/TweenManager/TweenManager';
import { BoardAnimationLayer } from 'js/bubbleCore/view/BoardAnimationLayer';
import { BoardSounds } from 'js/bubbleCore/view/BoardSounds';
import { SoundManager, MuteReason } from 'framework/SoundManager/SoundManager';
import { saveUserData } from "js/sdk/saveUserData";
import { Game } from 'framework/Game';
import {showInterstitial} from 'js/sdk/showInterstitial';
import { IGameData } from '../types';

interface IBubbleGameBoardViewConfig {
  gameBoard: BubbleGameBoard;
  hexSize: number;
  scale: number;
  verticalOffset: number;
  soundManager: SoundManager;
  game: Game;
}

export class BubbleGameBoardView {
  public animationLayer: BoardAnimationLayer;
  public readonly tweenManager: TweenManager = new TweenManager();
  public hexPixelSize: IHexPixelSize;

  private readonly layout: Layout;

  private readonly inputCatcher: InputCatcher;

  private readonly config: IBubbleGameBoardViewConfig;
  private readonly gameBoard: BubbleGameBoard;

  private readonly debugGrid: DebugGridView<Bubble>;
  private readonly debugSpace: DebugHexSpaceView<Bubble>;

  private shooterPointerView: ShooterPointerView;
  private shooterView: ShooterView;
  private bouncersView: BouncersView;
  private pointZonesView: PointZonesView;
  private bubblesView: BubblesView;
  private deathLineView: DeathLineView;

  private boardPixelSize: IRectangle;

  private game: Game;
  public isPaused: boolean = false;

  constructor(config: IBubbleGameBoardViewConfig) {
    this.layout = new Layout();
    this.config = config;
    this.gameBoard = config.gameBoard;
    this.inputCatcher = this.addChild(new InputCatcher(this));
    this.game = config.game;

    window.onblur = () => this.onBlur();
  }

  private onBlur(): void {
    if (this.isPaused === false) {
      this.togglePause();
    }
  }

  public resize(): void {
    const data: IGameData = this.getData();

    this.hexPixelSize = getHexPixelSize(this.config.hexSize, data.grid.type);
    this.boardPixelSize = getWholeBoardPixelSize(data, this.hexPixelSize);

    if (!this.animationLayer) {
      const { 
        pointZonesContainer,
        bouncersContainer,
        deathLineContainer,
        shooterContainer,
        shooterPointerContainer,
      } = this.layout;

      this.animationLayer = new BoardAnimationLayer(this.layout, this.tweenManager);
      this.pointZonesView = pointZonesContainer.addChild(new PointZonesView(data.popOutSpace, this.tweenManager));
      this.bouncersView = bouncersContainer.addChild(new BouncersView(data.popOutSpace));
      this.deathLineView = deathLineContainer.addChild(new DeathLineView(this));
      this.bubblesView = new BubblesView(this);
      this.shooterPointerView = shooterPointerContainer.addChild(new ShooterPointerView(this.getHexPixelSize(), data.shooter));
      this.shooterView = shooterContainer.addChild(new ShooterView(data.shooter, this, this.tweenManager));

      connectSignalToHasEventHandlers(
        this.gameBoard.signals.onEventProcessed,
        [
          this.pointZonesView,
          this.shooterView,
          this.bubblesView,
          this.bouncersView,
          this.deathLineView,
          new BoardSounds(),
        ]
      );
      this.adjustLayout(this.config, true);
    } else {
      this.adjustLayout(this.config, false);
    }

    this.shooterView.onModelUpdate(data.shooter);
    this.deathLineView.resize();
    this.pointZonesView.resize(data.popOutSpace, this.hexPixelSize);
    this.bouncersView.resize(data.popOutSpace, this.hexPixelSize);
    this.inputCatcher.resize();
  }

  private adjustLayout({scale, verticalOffset}: IBubbleGameBoardViewConfig, firstTime: boolean): void {
    const boardWidth = this.getBoardPixelSize().width;
    this.layout.boardBackground.width = boardWidth;
    this.layout.boardContainer.scale.set(scale);
    this.layout.boardContainer.pivot.x = boardWidth * 0.5;
    this.layout.boardContainer.pivot.y = this.getHexPixelSize().height * verticalOffset;
    this.layout.leftSpace.pivot.x = (boardWidth * scale * 0.5);
    this.layout.rightSpace.pivot.x = -(boardWidth * scale * 0.5);

    if (firstTime === false) {
      return;
    }

    const sfx = this.config.soundManager;

    const refreshSfxButton = () => {
      this.layout.soundLabelOn.visible = sfx.getSoundEnabled();
      this.layout.soundLabelOff.visible = !sfx.getSoundEnabled();
    };

    this.layout.soundButton.on('pointerdown', async () => {
      sfx.play("button_click");
      sfx.setSoundEnabled(!sfx.getSoundEnabled(), MuteReason.UserAction);
      refreshSfxButton();
      try {
        saveUserData({soundEnabled: sfx.getSoundEnabled()}); 
      } catch (e) {
        console.warn(e);
      }
      await this.game.togglePause();
      await showInterstitial();
      await this.game.togglePause();
    });

    this.layout.pauseButton.on('pointerdown', async () => {
      sfx.play("button_click");
      if(this.isPaused) {
        await showInterstitial();
      }

      this.togglePause();
    });

    refreshSfxButton();
  }

  private togglePause(): void {
    this.game.togglePause();
    this.isPaused = !this.isPaused;

    if(this.isPaused) {
      this.layout.pauseLabel.visible = false;
      this.layout.resumeLabel.visible = true;
    } else {
      this.layout.pauseLabel.visible = true;
      this.layout.resumeLabel.visible = false;
    }

    this.layout.pauseText.visible = this.isPaused;
    this.game.render();
  }

  public disableButtons(): void {
    this.layout.pauseButton.visible = false;
    this.layout.pauseLabel.visible = false;
    this.layout.resumeLabel.visible = false;

    this.layout.soundButton.visible = false;
    this.layout.soundLabelOn.visible = false;
    this.layout.soundLabelOff.visible = false;
  }

  public addChild<T extends PIXI.DisplayObject>(displayObject: T): T {
    return this.layout.boardContainer.addChild(displayObject);
  }

  public getRoot(): PIXI.Container {
    return this.layout.root;
  }

  public getBoardPixelSize(useScaleFactor: boolean = false): IRectangle {
    return this.boardPixelSize;
  }

  public getData() {
    return this.gameBoard.getData();
  }

  public getHexPixelSize(): IHexPixelSize {
    return this.hexPixelSize;
  }

  public getInputCatcher(): InputCatcher {
    return this.inputCatcher;
  }

  public getLayout(): Layout {
    return this.layout;
  }

  public displayTestShoot(result: ITestShootResult): void {
    this.shooterPointerView.updateView(result);
  }

  public update(delta: number): void {
    this.tweenManager.update(delta);
  }
}
